import React, { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Bug,
  Camera,
  CheckCircle2,
  Loader2,
  Lock,
  Send,
  Star,
  ShieldCheck,
  X,
  Upload,
} from "lucide-react";
import PageShell from "@/components/PageShell";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024; // 5 MB source file

const BetaFeedback = () => {
  const [step, setStep] = useState("gate"); // gate | form | success
  const [accessCode, setAccessCode] = useState("");
  const [gateChecking, setGateChecking] = useState(false);
  const [gateError, setGateError] = useState("");

  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState("");
  const [isBug, setIsBug] = useState(false);
  const [email, setEmail] = useState("");
  const [screenshotDataUrl, setScreenshotDataUrl] = useState("");
  const [screenshotMime, setScreenshotMime] = useState("");
  const [screenshotName, setScreenshotName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);

  const deviceInfo = useMemo(() => {
    if (typeof navigator === "undefined") return "";
    const ua = navigator.userAgent || "";
    const lang = navigator.language || "";
    const platform = navigator.platform || "";
    const viewport =
      typeof window !== "undefined"
        ? `${window.innerWidth}x${window.innerHeight}`
        : "";
    return `${ua} | platform=${platform} | lang=${lang} | viewport=${viewport}`.slice(
      0,
      1000
    );
  }, []);

  // ----- Gate submit -----
  const handleGateSubmit = async (e) => {
    e.preventDefault();
    setGateError("");
    const trimmed = accessCode.trim();
    if (!trimmed) {
      setGateError("Please enter your beta access code.");
      return;
    }
    setGateChecking(true);
    // We validate the code on submit (server enforces it too). Move to the form.
    setTimeout(() => {
      setGateChecking(false);
      setStep("form");
    }, 250);
  };

  // ----- Screenshot handlers -----
  const handleFilePicked = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, GIF, WEBP).");
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      toast.error("Screenshot must be under 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setScreenshotDataUrl(String(reader.result || ""));
      setScreenshotMime(file.type);
      setScreenshotName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const clearScreenshot = () => {
    setScreenshotDataUrl("");
    setScreenshotMime("");
    setScreenshotName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ----- Submit feedback -----
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (rating < 1 || rating > 5) {
      setFormError("Please select a rating.");
      return;
    }
    if (!message.trim()) {
      setFormError("Please write a short note about your experience.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/beta-feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_code: accessCode.trim(),
          rating,
          message: message.trim(),
          is_bug: isBug,
          tester_email: email.trim() || null,
          device_info: deviceInfo || null,
          screenshot_base64: screenshotDataUrl || null,
          screenshot_mime: screenshotMime || null,
        }),
      });
      if (res.status === 403) {
        setStep("gate");
        setGateError("That access code isn't recognized. Contact the SpendNova team.");
        setSubmitting(false);
        return;
      }
      if (res.status === 413) {
        setFormError("Screenshot is too large. Please attach a smaller image.");
        setSubmitting(false);
        return;
      }
      if (!res.ok) {
        const text = await res.text();
        setFormError(text || "Something went wrong. Please try again.");
        setSubmitting(false);
        return;
      }
      setStep("success");
    } catch (err) {
      setFormError(
        "Could not reach the SpendNova server. Check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetForNewSubmission = () => {
    setRating(0);
    setHoverRating(0);
    setMessage("");
    setIsBug(false);
    setEmail("");
    clearScreenshot();
    setFormError("");
    setStep("form");
  };

  return (
    <PageShell navyTheme={true}>
      <div className="relative mx-auto max-w-2xl px-4 md:px-6 py-14 md:py-20">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
          data-testid="beta-back-link"
        >
          <ArrowLeft size={14} /> Back to SpendNova
        </Link>

        {/* Header */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#22C585]">
            <ShieldCheck size={13} />
            Private · Beta Testers Only
          </div>
          <h1
            className="mt-3 text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
            data-testid="beta-title"
          >
            Beta Feedback & Bug Reports
          </h1>
          <p className="mt-3 text-[15px] text-[#C7D0DF] leading-relaxed">
            Everything you share here goes{" "}
            <span className="text-white font-semibold">directly to the SpendNova team</span>
            . No public App Store review. No third parties.
          </p>
        </div>

        {/* ============================================================
            STEP 1 — Access code gate
           ============================================================ */}
        {step === "gate" && (
          <form
            onSubmit={handleGateSubmit}
            className="mt-10 rounded-3xl p-6 md:p-8"
            style={{
              background:
                "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow:
                "0 40px 80px -40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            data-testid="beta-gate-form"
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#B7A5FF] font-bold">
              <Lock size={13} />
              Enter beta access code
            </div>
            <label
              htmlFor="beta-access-code"
              className="mt-4 block text-sm text-[#C7D0DF]"
            >
              This code is shared in your TestFlight welcome email or on your
              APK download page.
            </label>

            {/* Visible code helper — formality, not friction */}
            <div
              className="mt-3 rounded-2xl p-3.5 flex items-center gap-3"
              style={{
                background:
                  "linear-gradient(120deg, rgba(34,197,133,0.14) 0%, rgba(34,197,133,0.04) 100%)",
                border: "1px solid rgba(34,197,133,0.40)",
              }}
              data-testid="beta-code-helper"
            >
              <ShieldCheck size={16} className="text-[#22C585] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.20em] text-[#22C585] font-bold">
                  Testers, enter this code
                </div>
                <div
                  className="mt-0.5 text-[15px] font-extrabold text-white truncate"
                  style={{
                    fontFamily: "Source Code Pro, ui-monospace, monospace",
                    letterSpacing: "0.06em",
                  }}
                  data-testid="beta-code-visible"
                >
                  NOVA-BETA-2026
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAccessCode("NOVA-BETA-2026")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-[#04111F] shrink-0"
                style={{
                  background: "#22C585",
                  boxShadow:
                    "0 8px 20px -10px rgba(34,197,133,0.60), inset 0 1px 0 rgba(255,255,255,0.25)",
                }}
                data-testid="beta-code-prefill"
              >
                Prefill
              </button>
            </div>

            <input
              id="beta-access-code"
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="e.g. NOVA-BETA-2026"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="mt-3 w-full px-4 py-3 rounded-xl text-white bg-white/5 border focus:outline-none focus:ring-2"
              style={{
                borderColor: gateError
                  ? "rgba(239,107,107,0.55)"
                  : "rgba(255,255,255,0.20)",
                fontFamily: "Source Code Pro, ui-monospace, monospace",
                letterSpacing: "0.06em",
              }}
              data-testid="beta-access-code-input"
            />
            {gateError && (
              <div
                className="mt-2 text-[13px] text-[#EF6B6B]"
                data-testid="beta-gate-error"
              >
                {gateError}
              </div>
            )}
            <button
              type="submit"
              disabled={gateChecking}
              className="mt-5 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl text-[15px] font-semibold text-white disabled:opacity-60"
              style={{
                background: "#22C585",
                boxShadow:
                  "0 10px 30px -12px rgba(34,197,133,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
              }}
              data-testid="beta-gate-submit"
            >
              {gateChecking ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  Unlock feedback form <Send size={14} />
                </>
              )}
            </button>
          </form>
        )}

        {/* ============================================================
            STEP 2 — Feedback form
           ============================================================ */}
        {step === "form" && (
          <form
            onSubmit={handleSubmit}
            className="mt-10 space-y-5"
            data-testid="beta-feedback-form"
          >
            {/* Rating */}
            <fieldset
              className="rounded-3xl p-5 md:p-6"
              style={{
                background:
                  "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <legend className="px-2 text-[11px] uppercase tracking-[0.22em] text-[#22C585] font-bold">
                Your rating
              </legend>
              <div
                className="mt-2 flex items-center gap-1.5"
                onMouseLeave={() => setHoverRating(0)}
              >
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = (hoverRating || rating) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      onMouseEnter={() => setHoverRating(n)}
                      onFocus={() => setHoverRating(n)}
                      onClick={() => setRating(n)}
                      className="p-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C585]"
                      data-testid={`beta-rating-${n}`}
                    >
                      <Star
                        size={28}
                        strokeWidth={2}
                        className={active ? "text-[#F4C752]" : "text-white/25"}
                        fill={active ? "#F4C752" : "transparent"}
                      />
                    </button>
                  );
                })}
                <span className="ml-3 text-sm text-white/70">
                  {rating > 0 ? `${rating} / 5` : "Tap to rate"}
                </span>
              </div>
            </fieldset>

            {/* Bug toggle */}
            <div
              className="rounded-3xl p-4 md:p-5 flex items-center justify-between gap-4"
              style={{
                background: isBug
                  ? "linear-gradient(160deg, rgba(239,107,107,0.16) 0%, rgba(239,107,107,0.04) 100%)"
                  : "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                border: isBug
                  ? "1px solid rgba(239,107,107,0.45)"
                  : "1px solid rgba(255,255,255,0.12)",
                transition: "background 200ms ease, border-color 200ms ease",
              }}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: isBug
                      ? "rgba(239,107,107,0.18)"
                      : "rgba(255,255,255,0.06)",
                    border: isBug
                      ? "1px solid rgba(239,107,107,0.45)"
                      : "1px solid rgba(255,255,255,0.14)",
                  }}
                >
                  <Bug
                    size={17}
                    className={isBug ? "text-[#EF6B6B]" : "text-white/70"}
                  />
                </div>
                <div className="min-w-0">
                  <div
                    className="text-[14px] font-extrabold text-white"
                    style={{ fontFamily: "Sora, Inter, sans-serif" }}
                  >
                    Is this a bug or technical issue?
                  </div>
                  <div className="text-[12.5px] text-[#C7D0DF]">
                    Toggling this flags your report as a bug so the team sees it first.
                  </div>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isBug}
                onClick={() => setIsBug((v) => !v)}
                className="relative w-12 h-7 rounded-full shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22C585]"
                style={{
                  background: isBug ? "#EF6B6B" : "rgba(255,255,255,0.20)",
                }}
                data-testid="beta-bug-toggle"
              >
                <span
                  className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform"
                  style={{
                    transform: isBug ? "translateX(21px)" : "translateX(2px)",
                  }}
                />
              </button>
            </div>

            {/* Message */}
            <div
              className="rounded-3xl p-5 md:p-6"
              style={{
                background:
                  "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <label
                htmlFor="beta-message"
                className="text-[11px] uppercase tracking-[0.22em] text-[#22C585] font-bold"
              >
                Feedback or bug details
              </label>
              <textarea
                id="beta-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  isBug
                    ? "Steps to reproduce, what you expected, what actually happened…"
                    : "What worked, what didn't, feature requests, questions…"
                }
                rows={6}
                maxLength={5000}
                className="mt-2 w-full px-4 py-3 rounded-xl text-white bg-white/5 border focus:outline-none focus:ring-2 focus:ring-[#22C585] resize-y"
                style={{ borderColor: "rgba(255,255,255,0.20)" }}
                data-testid="beta-message-input"
              />
              <div className="mt-1 flex items-center justify-between text-[11px] text-white/50">
                <span>Text only. No sensitive account info please.</span>
                <span>{message.length} / 5000</span>
              </div>
            </div>

            {/* Email + screenshot */}
            <div
              className="rounded-3xl p-5 md:p-6"
              style={{
                background:
                  "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            >
              <label
                htmlFor="beta-email"
                className="text-[11px] uppercase tracking-[0.22em] text-[#B7A5FF] font-bold"
              >
                Email (optional)
              </label>
              <input
                id="beta-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tester@example.com — only if you want a reply"
                autoComplete="email"
                className="mt-2 w-full px-4 py-3 rounded-xl text-white bg-white/5 border focus:outline-none focus:ring-2 focus:ring-[#B7A5FF]"
                style={{ borderColor: "rgba(255,255,255,0.20)" }}
                data-testid="beta-email-input"
              />

              {/* Screenshot uploader */}
              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#B7A5FF] font-bold">
                  Attach a screenshot (optional)
                </div>
                {!screenshotDataUrl ? (
                  <label
                    htmlFor="beta-screenshot"
                    className="mt-2 flex items-center gap-3 px-4 py-4 rounded-xl cursor-pointer border-dashed border"
                    style={{
                      background: "rgba(183,165,255,0.06)",
                      borderColor: "rgba(183,165,255,0.40)",
                    }}
                    data-testid="beta-screenshot-picker"
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        background: "rgba(183,165,255,0.14)",
                        border: "1px solid rgba(183,165,255,0.40)",
                      }}
                    >
                      <Upload size={17} className="text-[#B7A5FF]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[14px] font-semibold text-white">
                        Click to choose an image
                      </div>
                      <div className="text-[12px] text-white/60">
                        PNG, JPG, GIF, or WEBP · up to 5 MB
                      </div>
                    </div>
                    <input
                      ref={fileInputRef}
                      id="beta-screenshot"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) =>
                        handleFilePicked(e.target.files && e.target.files[0])
                      }
                      data-testid="beta-screenshot-input"
                    />
                  </label>
                ) : (
                  <div
                    className="mt-2 rounded-xl overflow-hidden relative"
                    style={{
                      background: "rgba(4,17,31,0.4)",
                      border: "1px solid rgba(183,165,255,0.35)",
                    }}
                    data-testid="beta-screenshot-preview"
                  >
                    <img
                      src={screenshotDataUrl}
                      alt="Attached screenshot preview"
                      className="w-full max-h-72 object-contain"
                    />
                    <div
                      className="flex items-center justify-between px-3 py-2 text-[12px]"
                      style={{ borderTop: "1px solid rgba(183,165,255,0.25)" }}
                    >
                      <span className="text-white/70 truncate">
                        <Camera
                          size={12}
                          className="inline mr-1 text-[#B7A5FF]"
                        />
                        {screenshotName || "screenshot.png"}
                      </span>
                      <button
                        type="button"
                        onClick={clearScreenshot}
                        className="inline-flex items-center gap-1 text-[#EF6B6B] font-semibold hover:underline"
                        data-testid="beta-screenshot-remove"
                      >
                        <X size={12} />
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Error */}
            {formError && (
              <div
                className="rounded-xl px-4 py-3 text-[13px] text-[#EF6B6B]"
                style={{
                  background: "rgba(239,107,107,0.10)",
                  border: "1px solid rgba(239,107,107,0.40)",
                }}
                data-testid="beta-form-error"
              >
                {formError}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl text-base font-semibold text-white disabled:opacity-60"
              style={{
                background: "#22C585",
                boxShadow:
                  "0 12px 34px -12px rgba(34,197,133,0.65), inset 0 1px 0 rgba(255,255,255,0.20)",
              }}
              data-testid="beta-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send size={16} /> Send to SpendNova team
                </>
              )}
            </button>
            <p className="text-center text-[12px] text-white/50">
              Private submission · Goes only to the SpendNova team · Not shared publicly.
            </p>
          </form>
        )}

        {/* ============================================================
            STEP 3 — Success
           ============================================================ */}
        {step === "success" && (
          <div
            className="mt-10 rounded-3xl p-8 text-center"
            style={{
              background:
                "linear-gradient(160deg, rgba(34,197,133,0.18) 0%, rgba(34,197,133,0.04) 100%)",
              border: "1px solid rgba(34,197,133,0.45)",
              boxShadow:
                "0 40px 80px -40px rgba(34,197,133,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            data-testid="beta-success"
          >
            <div
              className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(34,197,133,0.22)",
                border: "1px solid rgba(34,197,133,0.55)",
              }}
            >
              <CheckCircle2 size={26} className="text-[#22C585]" />
            </div>
            <h2
              className="mt-4 text-2xl font-extrabold text-white"
              style={{ fontFamily: "Sora, Inter, sans-serif" }}
            >
              Thanks — got it!
            </h2>
            <p className="mt-2 text-[14px] text-[#E5EAF3]">
              Your feedback is on its way to the SpendNova team. If you left an
              email, we may follow up privately.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={resetForNewSubmission}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-semibold text-white"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.22)",
                }}
                data-testid="beta-send-another"
              >
                Send another report
              </button>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[14px] font-semibold text-white"
                style={{
                  background: "#22C585",
                  boxShadow:
                    "0 10px 30px -12px rgba(34,197,133,0.55), inset 0 1px 0 rgba(255,255,255,0.20)",
                }}
                data-testid="beta-back-home"
              >
                Back to SpendNova
              </Link>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default BetaFeedback;
